<template>
  <Portal v-if="isOpen">
    <ContextColorProvider context="brand">
      <div class="membership-terms">
        <LoaderIndicator
          v-if="loading"
          class="membership-terms__loader"
          data-testid="loading"
          with-container
        />
        <div v-if="data && !loading" class="membership-terms__container">
          <div class="membership-terms__header">
            <Heading :level="2" as="h2">
              <strong>{{ data.title }}</strong>
            </Heading>
            <BaseText :color="NebraskaColors.textSecondary" size="p4">
              {{ data.description }}
            </BaseText>
          </div>
          <div class="membership-terms__column">
            <ContentDisplay
              v-for="contract in data.contracts"
              :key="contract.code"
              :blocks-length="1"
              footer-label="Ler contrato"
              :footer-href="contract.action.url"
            >
              <template #content-block-1>
                <BaseText size="md" as="h3" :color="NebraskaColors.textPrimary">
                  <strong>{{ contract.title }}</strong>
                </BaseText>
                <BaseText :color="NebraskaColors.textSecondary" size="sm">
                  {{ contract.description }}
                </BaseText>
              </template>
            </ContentDisplay>
          </div>
        </div>
      </div>
      <div class="membership-terms__button-container">
        <ButtonContainer>
          <template #button-primary>
            <ButtonPrimary
              label="Aceito os termos"
              size="large"
              :loading="isSubmitting"
              @click="submit"
            />
          </template>
        </ButtonContainer>
      </div>
    </ContextColorProvider>
  </Portal>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import {
  getTerms,
  MembershipTermsResponse
} from '@/modules/common/services/http/account';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  BaseText,
  ButtonPrimary,
  ContextColorProvider,
  Heading,
  LoaderIndicator,
  ButtonContainer,
  ContentDisplay
} from '@warrenbrasil/nebraska-web';
import { trackPageView, trackAccepted } from './track-events';
import { Portal } from '@/modules/common/components/Portal';

const emit = defineEmits(['confirm']);
defineProps<{
  isOpen: boolean;
}>();

const isSubmitting = ref(false);
const loading = ref(false);
const data = ref<MembershipTermsResponse | null>(null);

const setData = async () => {
  loading.value = true;
  data.value = await getTerms();
  loading.value = false;
};

const submit = async () => {
  isSubmitting.value = true;
  trackAccepted();
  emit('confirm', { acceptedTerms: true });
};

onMounted(() => {
  setData();
  trackPageView();
});
</script>

<style lang="less" scoped>
@button-container-height: 97px;
.membership-terms {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  overflow-y: scroll;
  display: flex;
  justify-content: center;
  background: @background-secondary;
  z-index: 1;

  --footer-height: 150px;
  @media @mobileDown {
    --footer-height: 88px;
  }
  .content-display {
    background-color: @background-tertiary;
  }
  :deep(.footer-button__inner) {
    background-color: @background-tertiary;

    .base-text {
      color: @element-primary !important;
    }
  }
  &__main {
    padding-bottom: calc(var(--footer-height) + @size-spacing-x500);
  }
  &__header {
    width: 408px;
  }
  &__contract {
    background: @background-tertiary;
    border-radius: @size-spacing-x350;
    width: 492px;
    gap: @size-spacing-x500;

    &__link {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: @size-spacing-x900;
      margin-top: @size-spacing-x500 !important;
      border-top: 1px solid @divider-primary;
      > * {
        font-weight: 600;
      }
    }
  }
  &__container {
    margin: @size-spacing-x1800;
    max-width: 1008px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    height: fit-content;
    width: 100%;
  }
  &__button-container {
    width: 100%;
    position: fixed;
    bottom: 0;
    background: @background-secondary;
    z-index: 10;
  }
  &__column {
    display: flex;
    flex-direction: column;
    gap: @size-spacing-x400;
    width: 492px;
  }
  &__loader {
    height: calc(100vh - 64px);
  }
}
</style>
